import { useCallback, useEffect, useRef, useState } from 'react';

// Pré-carrega o logo da empresa em base64 e pré-decodifica a imagem.
// Resolve a race condition em iOS onde react-to-print dispara window.print()
// antes do <img> estar carregado/decodificado, gerando PDF sem logo.
//
// Uso:
//   const { logoBase64, waitForReady } = useLogoPreloader(company?.logo);
//   ...
//   onBeforeGetContent: async () => {
//     await waitForReady(4000); // espera até 4s, depois prossegue mesmo assim
//   }
export const useLogoPreloader = (logoPath) => {
  const [logoBase64, setLogoBase64] = useState(null);
  // Promise da carga atual. Trocada atomicamente quando logoPath muda,
  // evitando ambiguidade entre "ready do logo anterior" e "ready do novo".
  const readyPromiseRef = useRef(Promise.resolve());

  useEffect(() => {
    if (!logoPath) {
      setLogoBase64(null);
      readyPromiseRef.current = Promise.resolve();
      return;
    }

    setLogoBase64(null);
    let cancelled = false;
    const logoUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${logoPath}`;

    readyPromiseRef.current = fetch(logoUrl)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .then(async (dataUrl) => {
        if (cancelled) return;
        // Decode ANTES do setState para que, ao montar o <img> no DOM,
        // o WebKit já tenha o frame na cache e não atrase o snapshot do
        // react-to-print no iOS Safari.
        try {
          const img = new Image();
          img.src = dataUrl;
          await img.decode();
        } catch {
          // decode() pode falhar em browsers antigos; segue mesmo assim.
        }
        if (cancelled) return;
        setLogoBase64(dataUrl);
      })
      .catch((err) => {
        console.warn('useLogoPreloader: falha ao carregar logo:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [logoPath]);

  const waitForReady = useCallback(
    (timeoutMs = 4000) =>
      Promise.race([
        readyPromiseRef.current,
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]),
    []
  );

  return { logoBase64, waitForReady };
};
